import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';

import { WcgUploadFileRowComponent } from '../upload-file-row/upload-file-row.component';
import { MatButtonModule } from '@angular/material/button';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  defaultAllowedFileTypes,
  fileNameLengthValidator,
  fileSizeValidator,
  fileTypeValidator,
  forbiddenCharacters,
  forbiddenCharactersValidator,
} from '@shared/validators';

import { WcgButtonComponent } from '@wcg/frontend-framework';
import { DocumentType, RequiredDocumentType } from '@documents/models';
import { WcgUploadFileComponent } from '@shared/components/upload-file/upload-file.component';
import { Store } from '@ngrx/store';
import { WorkspaceType } from '@admin/questionnaire/models';
import { take } from 'rxjs/operators';
import { DocumentViewFacade } from '@submissions/v1/services';
import { selectSubmission } from '@submissions/v1/redux/submission.selectors';
import { findDocumentType } from '@data-access/submissions/utils/find-document-type.util';
import { findRequiredDocumentType } from '@submissions/v1/content/documents-sections/utils/documents-sections.utils';

@Component({
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgUploadFileRowComponent,
    WcgUploadFileComponent,
  ],
  selector: 'wcg-upload-requested-document',
  styleUrls: ['./upload-requested-document.component.scss'],
  templateUrl: './upload-requested-document.component.html',
})
export class WcgUploadRequestedDocumentComponent {
  private readonly _store = inject(Store);
  private readonly _fb = inject(FormBuilder);
  private readonly _documentViewFacade = inject(DocumentViewFacade);
  private readonly dialogRef = inject(
    MatDialogRef<WcgUploadRequestedDocumentComponent>,
  );
  public readonly data = inject<{
    multiSelect: boolean;
    isResponse: boolean;
    documentTypes: DocumentType[];
    requiredTypesForSubmission: RequiredDocumentType[];
    requiredDocument: RequiredDocumentType;
    threadId: string;
    msgEntityId: string;
    submissionId: string;
  }>(MAT_DIALOG_DATA);

  @ViewChild('titleRef')
  titleRef!: ElementRef<HTMLHeadingElement>;
  @ViewChild('uploadFileComponent')
  uploadFileComponent!: WcgUploadFileComponent;

  documentTypes: DocumentType[] = [];
  requiredTypesForSubmission: RequiredDocumentType[] = [];
  requiredDocument: RequiredDocumentType = {} as RequiredDocumentType;
  isResponse = false;
  threadId = '';
  msgEntityId = '';
  submissionId = '';

  allowedFileTypes = defaultAllowedFileTypes
    .map((type: string) => `.${type}`)
    .join(', ');

  forbiddenCharacters = forbiddenCharacters.join(', ');

  uploadedFileForm = this._fb.group({
    documentName: [''],
    documentType: [null as string | null, [Validators.required]],
    file: [
      null as File | null,
      [
        Validators.required,
        fileNameLengthValidator(256),
        fileSizeValidator(1),
        fileTypeValidator(),
        forbiddenCharactersValidator(),
      ],
    ],
    questionId: [''],
  });

  get uploadedFile(): FormControl {
    return this.uploadedFileForm.get('file') as FormControl;
  }

  get file(): FormControl {
    return this.uploadedFileForm.get('file') as FormControl;
  }

  constructor() {
    this.documentTypes = this.data.documentTypes;
    this.requiredTypesForSubmission = this.data.requiredTypesForSubmission;
    this.requiredDocument = {
      ...this.data.requiredDocument,
      optional: false,
      workspace_type: {} as WorkspaceType,
    };
    this.isResponse = this.data.isResponse ?? false;
    this.threadId = this.data.threadId;
    this.msgEntityId = this.data.msgEntityId;
    this.dialogRef.disableClose = true;
    this.submissionId = this.data.submissionId;
  }

  get documentName(): string {
    return (
      this.documentTypes.find(
        (type) =>
          type.id === this.uploadedFileForm.controls['documentType']?.value,
      )?.name || ''
    );
  }

  fileUpload(file: File[]) {
    if (file.length === 0) return;

    // Get document type information from required document
    const documentType = findDocumentType(
      this.requiredDocument.document_type?.id || '',
      this.documentTypes,
    );

    // Find required document type to get the questionId
    const requiredDocumentType =
      findRequiredDocumentType(
        documentType?.id || this.requiredDocument.document_type?.id || '',
        this.requiredTypesForSubmission,
      );

    this.uploadedFileForm.patchValue({
      documentName: requiredDocumentType?.document_type.title || file[0].name,
      documentType:
        documentType?.id || this.requiredDocument.document_type?.id || '',
      file: file[0],
      questionId: requiredDocumentType?.document_type.questionId || '',
    });
  }

  onDeleteFile(_: number) {
    this.uploadedFileForm.patchValue({
      documentName: '',
      documentType: null,
      file: null,
      questionId: '',
    });
    this.uploadFileComponent.deleteFile();
  }

  handleCancel() {
    this.dialogRef.close();
  }

  handleConfirm() {
    if (!this.uploadedFileForm.valid) {
      this.uploadedFileForm.markAllAsTouched();
      return;
    }

    this._store
      .select(selectSubmission)
      .pipe(take(1))
      .subscribe((submission) => {
        if (!submission) return;
        const formValue = this.uploadedFileForm.value;
        if (!formValue?.file || !formValue.documentType) return;

        const file = {
          documentName: formValue.documentName || '',
          documentType: formValue.documentType as string,
          file: formValue.file as File,
        };
        const documentType = findDocumentType(
          file.documentType,
          this.documentTypes,
        );

        const requiredDocumentType =
          findRequiredDocumentType(
            documentType?.id ?? '',
            this.requiredTypesForSubmission,
          );

        const document = {
          documentName: file.documentName,
          documentTypeId:
            documentType?.id || requiredDocumentType?.document_type.id || '',
          documentTypeName: documentType?.name || '',
          file: file.file,
          isAdditional: !requiredDocumentType,
          msgEntityId: this.msgEntityId,
          name: file.documentName,
          questionId:
            formValue.questionId ||
            requiredDocumentType?.document_type.questionId ||
            '',
          threadId: this.threadId,
        };

        this._documentViewFacade.uploadRequestedDocument(document);
        this.dialogRef.close();
      });
  }
}
